import { Injectable } from '@angular/core';
import { CanvasBranch, CanvasItem, CanvasNode, CanvasWorkflow } from './canvas-workflow.interface';
import { AbstractStepDTO, ConditionIfStepDTO, DelayStepDTO, EmailStepDTO, SmsStepDTO } from '@tj/tj-dmp-lib-api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {

  constructor(
    private translateService: TranslateService
  ){}

  /**
   * Crée un nouveau workflow avec une branche racine
   */
  createCanvasWorkflow(steps: AbstractStepDTO[]): CanvasWorkflow | null {
    if (steps.length > 0) {
      const workflow: CanvasWorkflow = {
        branchCounter: 1,
        rootBranch: this.createStartBranch(steps[0])
      };

      const mainBranch = this.createMainBranch(workflow);
      workflow.rootBranch.children.push(mainBranch);

      this.createCanvasBranch(steps.slice(1), mainBranch, workflow);
      return workflow;
    }

    return null;
  }

  /**
  * Crée la branche qui contient uniquement le point d'entrée
  */
  private createStartBranch(startStep): CanvasBranch {
    const startBranch: CanvasBranch = {
      id: 'branch-start',
      type: 'start',
      nodes: [],
      children: [],
      position: { x: 0, y: 0 },
      width: 0,
      height: 0,
      startRow: 0,
      endRow: 0,
      leftmostColumn: 0,
      rightmostColumn: 0,
      centerColumn: 0,
      nodeCounter: 1,
    };

    this.addStepToBranch(startStep, startBranch, 0, true);
    return startBranch;
  }

  /**
  * Crée la branche principale vide
  */
  private createMainBranch(workflow: CanvasWorkflow): CanvasBranch {
    workflow.branchCounter++;
    return {
      id: `branch-main`,
      type: 'main',
      nodes: [{
        id: 'branch1-node1',
        name: 'Sortie',
        step: {
          type: AbstractStepDTO.TypeEnum.END,
          complete: false,
        },
        isStartNode: false
      }],
      children: [],
      parentBranchId: 'branch-start',
      position: { x: 0, y: 0 },
      width: 0,
      height: 0,
      startRow: 0,
      endRow: 0,
      leftmostColumn: 0,
      rightmostColumn: 0,
      centerColumn: 0,
      nodeCounter: 0,
    };
  }

  /**
   * Construit récursivement une branche du workflow à partir d'une liste d'étapes.
   */
  private createCanvasBranch(steps: AbstractStepDTO[], branch: CanvasBranch, workflow: CanvasWorkflow) {
    for (let step of steps) {
      const newNode = this.addStepToBranch(step, branch, branch.nodes.length - 1);
      if (step.type === AbstractStepDTO.TypeEnum.CONDITIONIF) {
        const conditionStep = step as ConditionIfStepDTO;
        this.removeConditionalBranches(newNode, branch)
        this.createConditionalBranches(newNode, branch, workflow);
        const yesChild: CanvasBranch = branch.children.find(child => child.type === 'yes');
        const noChild: CanvasBranch = branch.children.find(child => child.type === 'no');
        this.createCanvasBranch(conditionStep.caseTrue, yesChild, workflow);
        this.createCanvasBranch(conditionStep.caseFalse, noChild, workflow);
      }
    }
  }

  /**
   * Retourne toutes les branches de manière récursive
   */
  getAllBranches(rootBranch: CanvasBranch): CanvasBranch[] {
    return this.getAllBranchesRecursive(rootBranch);
  }

  /**
   * Retourne tous les nœuds de manière récursive
   */
  getAllNodes(rootBranch: CanvasBranch): CanvasNode[] {
    return this.getAllNodesRecursive(rootBranch);
  }

  /**
   * Obtient tous les IDs de zones de drop
   */
  getAllDropIds(rootBranch: CanvasBranch): string[] {
    const allDropList: string[] = [];
    const allNodes = this.getAllNodes(rootBranch);
    const startId = rootBranch.nodes[0].id;
    
    allNodes.forEach(node => {
      if (node.id !== startId) {
        allDropList.push(`insert-before-${node.id}`);
      }
    });
    return allDropList;
  }

  /**
   * Crée un AbstractStepDTO à partir d'un CanvasItem
   */
  createStepFromItem(draggedItem: CanvasItem): AbstractStepDTO {

    const step: AbstractStepDTO =  {
      type: draggedItem.type,
      createdAt: new Date(),
      updatedAt: new Date(),
      complete: false
    };

    return step;
  }

  /**
   * Ajoute un nouvel élément à une branche à un index spécifique
   */
  addItemToBranch(draggedItem: CanvasItem, branch: CanvasBranch, insertIndex: number, workflow: CanvasWorkflow
  ): CanvasNode {

    const newStep = this.createStepFromItem(draggedItem);
    branch.nodeCounter++;
    const newNode: CanvasNode = {
      id: `${branch.id}-node${branch.nodeCounter}`,
      name: draggedItem.name,
      step: newStep,
      isStartNode: false,
    };

    branch.nodes.splice(insertIndex, 0, newNode);

    // Créer des branches conditionnelles si c'est une règle
    if (newStep.type === AbstractStepDTO.TypeEnum.CONDITIONIF) {
      this.removeConditionalBranches(newNode, branch);
      this.createConditionalBranches(newNode, branch, workflow);
    }

    return newNode;
  }

  /**
   * Supprime un élément d'un nœud
   */
  deleteNode(node: CanvasNode, branch: CanvasBranch): void {
    if (node.step) {
      if (node.step?.type === AbstractStepDTO.TypeEnum.CONDITIONIF) {
        this.removeConditionalBranches(node, branch);
      }
      const nodeIndex = branch.nodes.indexOf(node);
      const length = branch.nodes.length;
      branch.nodes.splice(nodeIndex, 1);
      if(nodeIndex === length-1 && branch.children.length === 0){
        branch.nodeCounter++;
        const endNode: CanvasNode = {
          id: `${branch.id}-node${branch.nodeCounter}`,
          name: 'Sortie',
          step: {
            type: AbstractStepDTO.TypeEnum.END,
            complete: false
          },
          isStartNode: false
        }
        branch.nodes.push(endNode);
      }
    }
  }

  /**
   * Crée des branches conditionnelles pour un nœud de condition
   */
  private createConditionalBranches(conditionNode: CanvasNode, parentBranch: CanvasBranch, workflow: CanvasWorkflow ): void {
    const yesBranch = this.createChildBranch(parentBranch, 'yes', conditionNode.id, workflow);
    const noBranch = this.createChildBranch(parentBranch, 'no', conditionNode.id, workflow);

    parentBranch.children.push(yesBranch, noBranch);
  }

  /**
   * Crée une branche enfant
   */
  private createChildBranch(parent: CanvasBranch, type: 'yes' | 'no', conditionNodeId: string, workflow: CanvasWorkflow): CanvasBranch {
    workflow.branchCounter++;
    const branch: CanvasBranch = {
      id: `branch${workflow.branchCounter}`,
      type: type,
      nodes: [{
        id: `branch${workflow.branchCounter}-node1`,
        name: 'Sortie',
        isStartNode: false,
        step: {
          type: AbstractStepDTO.TypeEnum.END,
          complete: false
        }
      }],
      children: [],
      parentBranchId: parent.id,
      parentConditionNodeId: conditionNodeId,
      position: { x: 0, y: 0 },
      width: 0,
      height: 0,
      startRow: 0,
      endRow: 0,
      leftmostColumn: 0,
      rightmostColumn: 0,
      centerColumn: 0,
      nodeCounter: 1
    };

    return branch;
  }

  /**
   * Supprime les branches conditionnelles d'un nœud
   */
  private removeConditionalBranches(node: CanvasNode, branch: CanvasBranch): void {
    const indexNode = branch.nodes.indexOf(node);
    if (indexNode === -1) return;
    branch.nodes.splice(indexNode + 1);
    branch.children = [];
  }

  /**
   * Vérifie si un nœud peut être déplacé
   */
  isDragDisabled(node: CanvasNode): boolean {
    return ([AbstractStepDTO.TypeEnum.CONDITIONIF, AbstractStepDTO.TypeEnum.END].includes(node.step.type) || node.isStartNode);
  }

  /**
   * Méthodes utilitaires privées
   */
  private getAllBranchesRecursive(branch: CanvasBranch): CanvasBranch[] {
    const branches = [branch];
    branch.children.forEach(child => {
      branches.push(...this.getAllBranchesRecursive(child));
    });
    return branches;
  }

  private getAllNodesRecursive(branch: CanvasBranch): CanvasNode[] {
    let nodes = [...branch.nodes];
    branch.children.forEach(child => {
      nodes.push(...this.getAllNodesRecursive(child));
    });
    return nodes;
  }

  /**
   * Crée un CanvasNode à partir d'un AbstractStepDTO
   */
  private createNodeFromStep(step: AbstractStepDTO, branch: CanvasBranch, isStartStep?: boolean): CanvasNode {
    branch.nodeCounter++;
    return {
      id: `${branch.id}-node${branch.nodeCounter}`,
      name: this.getStepDisplayName(step),
      step: step,
      isStartNode: isStartStep || false
    };
  }

  /**
   * Ajoute un step à une branche
   */
  private addStepToBranch(step: AbstractStepDTO, branch: CanvasBranch, insertIndex: number, isStartStep?: boolean): CanvasNode {
    const newNode = this.createNodeFromStep(step, branch, isStartStep);
    branch.nodes.splice(insertIndex, 0, newNode);
    return newNode;
  }

/**
 * Retourne le nom d'affichage d'un step
 */
  private getStepDisplayName(step: AbstractStepDTO): string {
    return step.type ? this.translateService.instant(`workflow.board.sourceItem.${step.type}`) : "";
  }

  /**
   * Crée les AbstractStepDTO à partir d'une branche
   */
  createStepsFromBranch(branch: CanvasBranch): AbstractStepDTO[] {
    const stepDTOs: AbstractStepDTO[] = [];

    branch.nodes.forEach((node) => {
      if(node.step.type !== AbstractStepDTO.TypeEnum.END)
      stepDTOs.push({
        ...node.step,
        type: node.step.type
      });
    });

    if (branch.children.length === 1) {
      stepDTOs.push(...this.createStepsFromBranch(branch.children[0]));
    } else if (branch.children.length === 2) {
      const conditionStep = stepDTOs[stepDTOs.length - 1] as ConditionIfStepDTO;
      conditionStep.type = AbstractStepDTO.TypeEnum.CONDITIONIF;

      const trueBranch = branch.children.find(child => child.type === 'yes');
      const falseBranch = branch.children.find(child => child.type === 'no');

      if (trueBranch) {
        conditionStep.caseTrue = this.createStepsFromBranch(trueBranch);
      }
      if (falseBranch) {
        conditionStep.caseFalse = this.createStepsFromBranch(falseBranch);
      }
    }

    return stepDTOs;
  }

}